import type {
  ICollectionTeaser,
  ITeaser,
  TArticle,
  TArticleType,
  TTarget,
} from '~~/types/local_types';
import useGetUrl from '~/composables/useArticleUrl';
import useIsChildPageOf from '~/composables/useIsChildPageOf';

const useArticleData = function (article: TArticle | ITeaser | ICollectionTeaser) {
  const getTitle = () => {
    return article.teaserTitle || article.title;
  };

  const getImage = () => {
    const missingImage = { id: 'unknown' };

    return article.teaserImage || missingImage;
  };

  const getIntro = (): string | undefined => {
    // flatten intro or teaser text to use in the teaser
    return useMapTextBlocks(article.teaserText?.blocks);
  };

  const getLabel = () => {
    // if the article type matches the parent, we do not show a label.
    const parent = useIsChildPageOf();

    if (parent !== '' && article.articleType?.includes(parent)) return undefined;

    const mappings: { [key in TArticleType]: string | undefined } = {
      peilingtrends: 'Peilingtrend',
      'eenvandaag-vraagt': 'EenVandaag Vraagt',
      uitlegvideos: 'Uitlegvideo',
      'opiniepanel/uitslagen': 'Opiniepanel',
      'opiniepanel/paginas': 'Opiniepanel',
      'doe-mee': 'Doe mee',
      paginas: undefined,
      'externe-link': undefined,
      artikelen: undefined,
      'shorts': 'Short'
    };

    if (article.articleType) {
      return mappings[article.articleType as keyof typeof mappings];
    }

    return undefined;
  };

  const getUrl = (): string | undefined => {
    if (!article.slug || (!article.articleType && !article.collection)) return article.url;
    return useGetUrl(article.articleType || article.collection, article.slug, article.externalUrl);
  };

  const getUrlTarget = (): TTarget | undefined => {
    const requestUrl = useRequestURL();
    const pageUrl = new URL(requestUrl);

    const url = getUrl();

    if (!url) return;

    if (url.includes(pageUrl.host) || !url.startsWith('http')) {
      return '_self';
    }
    return '_blank';
  };

  const getUrlRel = (): string => {
    const target = getUrlTarget();
    return target === '_blank' ? 'noopener noreferrer' : '';
  };

  const getMeta = () => {
    const meta: { type: string; data: string | undefined }[] = [];

    if (article.articleType === 'paginas') {
      return meta;
    }

    meta.push({
      type: 'publishDate',
      data: article.publishTime,
    });

    return meta;
  };

  const isExternal = () => {
    return article.articleType === 'externe-link';
  };

  const isVideo = () => {
    return article.articleType === 'uitlegvideos' || article.isVideo;
  };

  const getDate = () => {
    return article.adjustTime ? article.adjustTime : article.publishTime;
  };

  const getFormattedDate = () => {
    const date = getDate();
    // Ensure the input value is valid
    if (!date || isNaN(new Date(date).getTime())) {
      return 'Invalid Date'; // Fallback text for when the date is invalid
    }

    // Format the date using Intl.DateTimeFormat
    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit', // Two-digit day (e.g., 01, 02)
      month: 'short', // Short month name (e.g., Jan)
      year: 'numeric',
    }).format(new Date(date));
  };

  return reactive({
    title: getTitle(),
    intro: getIntro(),
    image: getImage(),
    label: getLabel(),
    url: getUrl(),
    target: getUrlTarget(),
    rel: getUrlRel(),
    meta: getMeta(),
    external: isExternal(),
    video: isVideo(),
    date: getDate(),
    formattedDate: getFormattedDate(),
  });
};

export default useArticleData;
